import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Volume2 } from "lucide-react";
import { IDS } from "@/lib/testIds";
import { speak } from "@/lib/tts";

export default function Hero() {
  const scrollTracks = () => {
    document.getElementById("tracks")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid={IDS.hero.root}
      className="relative overflow-hidden"
    >
      {/* Decorative blobs */}
      <div className="phonx-blob absolute -top-20 -left-20 w-96 h-96 rounded-full bg-sky-300"></div>
      <div className="phonx-blob absolute top-40 -right-24 w-96 h-96 rounded-full bg-amber-300"></div>
      <div className="phonx-blob absolute bottom-0 left-1/2 w-96 h-96 rounded-full bg-rose-200"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-20 sm:pt-24 sm:pb-28 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-slate-200 rounded-full px-4 py-1.5 text-xs font-bold text-sky-600 uppercase tracking-widest mb-6 shadow-sm">
              <Sparkles className="w-3.5 h-3.5" /> Read confidently with phonics
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
              Learn to read <br />
              with{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-sky-500">sounds</span>
                <svg
                  className="absolute -bottom-2 left-0 w-full"
                  height="14"
                  viewBox="0 0 200 14"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M2 8 Q 50 2, 100 7 T 198 6"
                    stroke="#FBBF24"
                    strokeWidth="5"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              , <br className="hidden sm:block" />
              pictures & words.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed">
              PhonX is a friendly phonics playground. Tap a letter, hear the sound,
              see the picture — then practice with playful mini-games. Two tracks so
              everyone finds their level.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                data-testid={IDS.hero.ctaBasic}
                onClick={scrollTracks}
                className="rounded-full px-8 py-4 bg-sky-500 text-white font-bold hover:bg-sky-600 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
              >
                Start learning <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="hero-hear-sample"
                onClick={() => speak("A is for Apple. B is for Ball. Welcome to Phon X.")}
                className="rounded-full px-8 py-4 bg-white border-2 border-slate-200 text-slate-800 font-bold hover:border-sky-400 hover:text-sky-600 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Volume2 className="w-4 h-4" /> Hear a sample
              </button>
            </div>

            <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                <span className="font-semibold">Free forever</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-sky-500"></div>
                <span className="font-semibold">No login required</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                <span className="font-semibold">Made for all ages</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="phonx-float relative bg-white rounded-[2.5rem] p-8 shadow-[0_20px_60px_rgb(0,0,0,0.08)] border-2 border-white">
                <div className="text-center">
                  <div className="text-7xl sm:text-8xl mb-2">🍎</div>
                  <div className="font-display text-8xl sm:text-9xl font-semibold text-sky-500 leading-none">
                    Aa
                  </div>
                  <div className="mt-3 text-lg font-bold text-slate-500 uppercase tracking-widest">
                    /a/ · Apple
                  </div>
                  <button
                    data-testid="hero-play-a"
                    onClick={() => speak("A. /a/. Apple.")}
                    className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-3 bg-sky-100 hover:bg-sky-200 text-sky-700 font-bold transition-colors"
                  >
                    <Volume2 className="w-4 h-4" /> Play sound
                  </button>
                </div>
              </div>
              <div className="absolute -top-6 -right-4 w-20 h-20 bg-amber-300 rounded-3xl flex items-center justify-center text-4xl rotate-12 shadow-lg">
                🎯
              </div>
              <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-rose-300 rounded-3xl flex items-center justify-center text-4xl -rotate-12 shadow-lg">
                🎨
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
